import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from './LandingNav';
import Footer from './Footer';

const Features = () => {
  const mainFeatures = [
    {
      image: '/images/all/ai-protection-visual.png',
      title: 'AI-Powered Threat Detection',
      description: 'Advanced artificial intelligence continuously monitors your wallet for suspicious activities, unauthorized access attempts, and emerging security threats in real-time.',
      features: [
        'Real-time wallet monitoring',
        'Behavioral threat analysis',
        'Automated security alerts',
        'Predictive risk assessment'
      ],
      cta: 'Secure Wallet'
    },
    {
      image: '/images/all/recovery-phrase-security.png',
      title: 'Military-Grade Encryption',
      description: 'Your private keys and sensitive data are protected with AES-256 encryption, ensuring bank-level security for all your crypto assets and transactions.',
      features: [
        'End-to-end encryption',
        'Zero-knowledge architecture',
        'Hardware security modules',
        'Multi-layer key protection'
      ],
      cta: 'Encrypt Assets'
    },
    {
      image: '/images/all/backup-system-illustration.png',
      title: 'Automated Asset Backup',
      description: 'Never lose access to your crypto assets. Our intelligent backup system securely stores your recovery phrases across multiple encrypted locations.',
      features: [
        'Multi-location backup',
        'Encrypted cloud storage',
        'Automated scheduling',
        'Instant recovery access'
      ],
      cta: 'Backup Wallet'
    }
  ];

  const additionalFeatures = [
    {
      icon: '/images/all/multi-wallet-connection.png',
      title: 'Multi-Wallet Integration',
      description: 'Connect all your wallets in one secure platform. Support for MetaMask, Trust Wallet, Coinbase, and 100+ other wallets.',
      cta: 'Connect Wallet'
    },
    {
      icon: '/images/all/biometric-authentication.png',
      title: 'Biometric Security',
      description: 'Advanced biometric authentication including fingerprint, face recognition, and voice verification for ultimate protection.',
      cta: 'Enable Biometrics'
    },
    {
      icon: '/images/all/cross-chain-bridge.png',
      title: 'Cross-Chain Support',
      description: 'Secure assets across multiple blockchain networks with seamless cross-chain transaction monitoring.',
      cta: 'Explore Chains'
    },
    {
      icon: '/images/all/recovery-phrase-security.png',
      title: 'Recovery Phrase Protection',
      description: 'Advanced recovery phrase management with encrypted storage and secure backup protocols.',
      cta: 'Secure Phrase'
    },
    {
      icon: '/images/all/transaction-monitor.png',
      title: 'Transaction Monitoring',
      description: 'Track all wallet activities across chains with real-time alerts for every transaction.',
      cta: 'Monitor Wallet'
    },
    {
      icon: '/images/all/cold-storage-vault.png',
      title: 'Cold Storage Vault',
      description: 'Keep your private keys completely offline while maintaining instant access when needed.',
      cta: 'Create Vault'
    }
  ];

  return (
    <div className="min-h-screen bg-deepspace overflow-x-hidden">
      <LandingNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-purple/10 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
            Comprehensive
            <span className="block bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
              Wallet Security Features
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Protect, backup, and recover your crypto assets with enterprise-grade security features designed for Web3.
          </p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400 overflow-hidden">
        <div className="max-w-7xl mx-auto space-y-24">
          {mainFeatures.map((feature, index) => (
            <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
              <div 
                className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}
                data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-3xl blur-2xl"></div>
                  <div className="relative bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8">
                    <img src={feature.image} alt={feature.title} className="w-full h-auto object-contain" />
                  </div>
                </div>
              </div>

              <div 
                className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}
                data-aos={index % 2 === 0 ? "fade-left" : "fade-right"}
                data-aos-delay="200"
              >
                <h3 className="text-3xl lg:text-4xl font-bold text-text-primary">{feature.title}</h3>
                <p className="text-lg text-text-secondary leading-relaxed">{feature.description}</p>
                
                <div className="space-y-3">
                  {feature.features.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-cyber-blue rounded-full"></div>
                      <span className="text-text-primary">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/auth" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-semibold rounded-xl hover:scale-105 transition-all duration-300">
                  {feature.cta}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              More Security Features
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Additional layers of protection to keep your digital assets secure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 hover:border-cyber-blue/50 transition-all duration-300 hover:scale-105 shadow-[0_8px_30px_rgb(0,0,0,0.5)]"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-2xl flex items-center justify-center mb-6">
                  <img src={feature.icon} alt={feature.title} className="w-10 h-10 object-contain filter brightness-0 invert opacity-80" />
                </div>
                <h3 className="text-xl font-bold text-text-primary mb-3">{feature.title}</h3>
                <p className="text-text-secondary mb-6 leading-relaxed">{feature.description}</p>
                <Link to="/auth" className="text-cyber-blue font-semibold hover:text-cyber-purple transition-colors duration-300 inline-flex items-center">
                  {feature.cta}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-cyber-blue/10 via-cyber-purple/10 to-cyber-green/10 backdrop-blur-sm border border-cyber-blue/30 rounded-3xl p-12 text-center" data-aos="zoom-in">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Ready to Secure Your Crypto Assets?
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Start protecting your digital wealth with enterprise-grade Web3 security.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
                Connect Wallet
              </Link>
              <Link to="/auth" className="px-8 py-4 border border-cyber-blue/50 text-cyber-blue font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-blue/10 transition-all duration-300 inline-block text-center">
                Create Wallet
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;